import {
  DataSyncHealthStatus,
  DataSyncMetricsBucket,
  DataSyncMetricsSummary,
} from '../types/DataSyncMetrics';

export type MetricsGranularity = 'METRICS_GRANULARITY_HOURLY' | 'METRICS_GRANULARITY_DAILY';

const HOUR = 60 * 60;
const DAY = 24 * HOUR;

// Sentinel returned by `bucketKeyForValue` when an API bucket string can't be
// parsed as a date; keeps bad rows from poisoning real bucket totals.
const UNPARSEABLE_BUCKET_KEY = '__metrics_unparseable_bucket__';

// Canonical map from a bucket-start epoch to the string key used everywhere
// (timeline + per-bucket totals). Always UTC ISO so keys are TZ-stable.
const bucketKeyForEpoch = (epoch: number) => new Date(epoch * 1000).toISOString();

const stepForGranularity = (granularity: MetricsGranularity): number => (
  granularity === 'METRICS_GRANULARITY_HOURLY' ? HOUR : DAY
);

const alignEpochToBucketStart = (epoch: number, granularity: MetricsGranularity): number => {
  if (granularity === 'METRICS_GRANULARITY_HOURLY') {
    return Math.floor(epoch / HOUR) * HOUR;
  }

  const date = new Date(epoch * 1000);
  date.setUTCHours(0, 0, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

// Convert an API-supplied bucket string into the canonical key by parsing it,
// snapping to the start of its bucket in UTC, then re-encoding via
// `bucketKeyForEpoch`. This is what makes lookups TZ- and format-stable.
const bucketKeyForValue = (bucket: string, granularity: MetricsGranularity): string => {
  const parsed = new Date(bucket).getTime();
  if (Number.isNaN(parsed)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[metricsUtils] dropping unparseable bucket value:', bucket);
    }
    return UNPARSEABLE_BUCKET_KEY;
  }

  const alignedEpoch = alignEpochToBucketStart(Math.floor(parsed / 1000), granularity);
  return bucketKeyForEpoch(alignedEpoch);
};

// Snap a [from, to] epoch range to the bucket starts that bound it. Callers
// pass user-facing ranges (e.g. day-end at 23:59:59); this returns the bucket
// boundaries the chart and previous-period math actually operate on.
export const normalizeRangeForGranularity = (
  fromEpoch: number,
  toEpoch: number,
  granularity: MetricsGranularity
): { fromEpoch: number; toEpoch: number } => {
  const normalizedFrom = alignEpochToBucketStart(fromEpoch, granularity);
  const normalizedTo = alignEpochToBucketStart(toEpoch, granularity);

  if (normalizedTo < normalizedFrom) {
    return { fromEpoch: normalizedFrom, toEpoch: normalizedFrom };
  }

  return {
    fromEpoch: normalizedFrom,
    toEpoch: normalizedTo,
  };
};

const buildBucketTimeline = (
  fromEpoch: number,
  toEpoch: number,
  granularity: MetricsGranularity
): string[] => {
  const normalized = normalizeRangeForGranularity(fromEpoch, toEpoch, granularity);
  const step = stepForGranularity(granularity);
  const timeline: string[] = [];

  for (let epoch = normalized.fromEpoch; epoch <= normalized.toEpoch; epoch += step) {
    timeline.push(bucketKeyForEpoch(epoch));
  }

  return timeline;
};

// Project API buckets onto a dense, gap-free numeric series across the range.
// Missing buckets become 0 so sparklines and line charts stay continuous.
export const buildBucketSeriesForRange = (
  buckets: DataSyncMetricsBucket[],
  valueSelector: (bucket: DataSyncMetricsBucket) => number,
  fromEpoch: number,
  toEpoch: number,
  granularity: MetricsGranularity
): number[] => {
  const totalsByBucket = new Map<string, number>();

  buckets.forEach((bucket) => {
    const key = bucketKeyForValue(bucket.bucket || '', granularity);
    totalsByBucket.set(key, (totalsByBucket.get(key) || 0) + valueSelector(bucket));
  });

  return buildBucketTimeline(fromEpoch, toEpoch, granularity)
    .map((bucketKey) => totalsByBucket.get(bucketKey) || 0);
};

export interface BucketPoint {
  bucket: string;
  value: number;
}

// Same as buildBucketSeriesForRange but tags each point with its bucket key,
// for charts that need to render an x-axis label per point.
export const buildBucketPointsForRange = (
  buckets: DataSyncMetricsBucket[],
  valueSelector: (bucket: DataSyncMetricsBucket) => number,
  fromEpoch: number,
  toEpoch: number,
  granularity: MetricsGranularity
): BucketPoint[] => {
  const totalsByBucket = new Map<string, number>();

  buckets.forEach((bucket) => {
    const key = bucketKeyForValue(bucket.bucket || '', granularity);
    totalsByBucket.set(key, (totalsByBucket.get(key) || 0) + valueSelector(bucket));
  });

  return buildBucketTimeline(fromEpoch, toEpoch, granularity)
    .map((bucketKey) => ({
      bucket: bucketKey,
      value: totalsByBucket.get(bucketKey) || 0,
    }));
};

const toNumber = (value?: number | string): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const toPercentage = (value: number): number => Math.max(0, Math.min(100, value));

const getHealthStatus = (errorRate: number): DataSyncHealthStatus => {
  if (errorRate <= 0) {
    return DataSyncHealthStatus.HEALTHY;
  }

  if (errorRate <= 10) {
    return DataSyncHealthStatus.WARNING;
  }

  return DataSyncHealthStatus.FAILING;
};

// Aggregate raw buckets into a per-data-sync summary (totals, success/error
// rates, sparkline, health). When `options` includes a range + granularity,
// the sparkline is gap-padded across that range; otherwise it is the buckets
// observed in the input, sorted chronologically.
export const computeMetricsSummaries = (
  buckets: DataSyncMetricsBucket[],
  options?: {
    fromEpoch?: number;
    toEpoch?: number;
    granularity?: MetricsGranularity;
  }
): Map<string, DataSyncMetricsSummary> => {
  const groupedByDataSync = new Map<string, DataSyncMetricsBucket[]>();

  for (const bucket of buckets) {
    const dataSyncId = bucket.datasync_id;
    if (!dataSyncId) {
      continue;
    }

    const entries = groupedByDataSync.get(dataSyncId) || [];
    entries.push(bucket);
    groupedByDataSync.set(dataSyncId, entries);
  }

  const summaries = new Map<string, DataSyncMetricsSummary>();
  const granularity = options?.granularity;
  const hasRange = typeof options?.fromEpoch === 'number' && typeof options?.toEpoch === 'number';

  groupedByDataSync.forEach((entries, datasyncId) => {
    const bucketTotals = new Map<string, { success: number; errors: number }>();

    for (const entry of entries) {
      const key = granularity
        ? bucketKeyForValue(entry.bucket || '', granularity)
        : (entry.bucket || '');
      const totals = bucketTotals.get(key) || { success: 0, errors: 0 };
      totals.success += toNumber(entry.total_success_count);
      totals.errors += toNumber(entry.total_error_count);
      bucketTotals.set(key, totals);
    }

    const sortedBuckets = Array.from(bucketTotals.entries())
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());

    const sparklineData = hasRange && granularity
      ? buildBucketTimeline(options.fromEpoch as number, options.toEpoch as number, granularity)
        .map((bucketKey) => {
          const totals = bucketTotals.get(bucketKey) || { success: 0, errors: 0 };
          return totals.success;
        })
      : sortedBuckets.map(([, totals]) => totals.success);
    const totalSuccess = sortedBuckets.reduce((sum, [, totals]) => sum + totals.success, 0);
    const totalErrors = sortedBuckets.reduce((sum, [, totals]) => sum + totals.errors, 0);
    const totalEvents = totalSuccess + totalErrors;

    const successRate = totalEvents > 0 ? toPercentage((totalSuccess / totalEvents) * 100) : 0;
    const errorRate = totalEvents > 0 ? toPercentage((totalErrors / totalEvents) * 100) : 0;

    summaries.set(datasyncId, {
      datasyncId,
      totalEvents,
      totalErrors,
      successRate,
      errorRate,
      sparklineData,
      healthStatus: getHealthStatus(errorRate),
    });
  });

  return summaries;
};
